import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ChatAttachment,
  ChatConversationListItem,
  ChatMessage,
  ChatWsServerFrame,
} from '@driverforce/shared-types';
import { api } from '../services/api';
import { adminChatWs } from '../services/chatWs';
import { useAuth } from '../hooks/useAuth';
import { Forbidden } from '../components/Forbidden';
import { toastApiError } from '../lib/apiToast';

const PAGE_SIZE = 30;

// Ported to client cool-grey + violet-260 palette (see admin index.css).
const INK = '#262A35';
const PAPER = '#FBFBFC';
const AMBER = '#4D6DD8';
const RULE = '#DBDCE0';
const RULE_STRONG = '#C4C7CD';
const MUTED = '#8C8F98';

const initialsOf = (first: string | null, last: string | null, email: string): string => {
  const fi = first?.[0]?.toUpperCase() ?? '';
  const li = last?.[0]?.toUpperCase() ?? '';
  if (fi || li) return `${fi}${li}` || '·';
  return email[0]?.toUpperCase() ?? '·';
};

const fmtRel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const fmtTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

interface ThreadMessagesState {
  loading: boolean;
  messages: ChatMessage[];
  hasMore: boolean;
}

export const ChatsPage: React.FC = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;

  const [conversations, setConversations] = useState<ChatConversationListItem[] | null>(null);
  const [error, setError] = useState<'forbidden' | 'error' | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [draft, setDraft] = useState('');
  // Mobile only: below md the list and the thread swap in place of sitting
  // side by side. Opening a conversation shows the thread; ← goes back.
  const [threadOpen, setThreadOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [threadState, setThreadState] = useState<Record<string, ThreadMessagesState>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Debounced search input.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  // Token wiring for REST calls (WS is wired in AdminLayout).
  useEffect(() => {
    api.setToken(token);
  }, [token]);

  const loadConversations = useCallback(async () => {
    if (!isAdmin) {
      setError('forbidden');
      return;
    }
    const res = await api.listChatConversations({
      filter,
      search: debouncedSearch || undefined,
      page: 1,
      pageSize: PAGE_SIZE,
    });
    if (res.success && res.data) {
      setConversations(res.data.items);
      setError(null);
      setActiveId((curr) => curr ?? res.data!.items[0]?.id ?? null);
      return;
    }
    if (res.error?.code === 'FORBIDDEN') {
      setError('forbidden');
      return;
    }
    toastApiError(res, 'Failed to load conversations');
    setError('error');
  }, [isAdmin, filter, debouncedSearch]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  // Load messages on conversation selection.
  const loadMessages = useCallback(
    async (conversationId: string) => {
      setThreadState((s) => ({
        ...s,
        [conversationId]: {
          loading: true,
          messages: s[conversationId]?.messages ?? [],
          hasMore: s[conversationId]?.hasMore ?? true,
        },
      }));
      const res = await api.listChatConversationMessages(conversationId, { limit: 50 });
      if (res.success && res.data) {
        setThreadState((s) => ({
          ...s,
          [conversationId]: {
            loading: false,
            messages: res.data!.messages,
            hasMore: res.data!.hasMore,
          },
        }));
      }
    },
    [],
  );

  useEffect(() => {
    if (!activeId) return;
    if (!threadState[activeId]) {
      void loadMessages(activeId);
    }
  }, [activeId, loadMessages, threadState]);

  // Mark read when the active thread's tail changes. Depend on the latest
  // message id ONLY (not the whole threadState map) so unrelated thread
  // broadcasts don't re-fire a POST for the active conversation.
  const activeLatestId = activeId
    ? threadState[activeId]?.messages.at(-1)?.id
    : undefined;
  const activeLatestSender = activeId
    ? threadState[activeId]?.messages.at(-1)?.senderType
    : undefined;
  useEffect(() => {
    if (!activeId || !activeLatestId) return;
    if (activeLatestSender === 'admin') return;
    void api.markChatConversationRead(activeId, activeLatestId);
    setConversations((prev) =>
      prev
        ? prev.map((c) => (c.id === activeId ? { ...c, unreadCountAdmin: 0 } : c))
        : prev,
    );
  }, [activeId, activeLatestId, activeLatestSender]);

  // WebSocket fan-in.
  useEffect(() => {
    const off = adminChatWs.onMessage((frame: ChatWsServerFrame) => {
      if (frame.type === 'message.created') {
        const { conversationId, message } = frame;
        setThreadState((s) => {
          const existing = s[conversationId];
          if (!existing) return s;
          if (existing.messages.some((m) => m.id === message.id)) return s;
          // Dedup against admin's own optimistic copy by clientNonce.
          if (message.clientNonce) {
            const idx = existing.messages.findIndex(
              (m) => m.clientNonce === message.clientNonce,
            );
            if (idx >= 0) {
              const next = existing.messages.slice();
              next[idx] = message;
              return { ...s, [conversationId]: { ...existing, messages: next } };
            }
          }
          return {
            ...s,
            [conversationId]: {
              ...existing,
              messages: [...existing.messages, message],
            },
          };
        });
        setConversations((prev) =>
          prev
            ? prev
                .map((c) =>
                  c.id === conversationId
                    ? {
                        ...c,
                        lastMessageAt: message.createdAt,
                        lastMessagePreview:
                          message.body?.slice(0, 280) ??
                          (message.attachments.length > 0 ? '📎 attachment' : ''),
                        unreadCountAdmin:
                          message.senderType === 'user' && c.id !== activeId
                            ? c.unreadCountAdmin + 1
                            : c.unreadCountAdmin,
                      }
                    : c,
                )
                .sort((a, b) => (a.lastMessageAt < b.lastMessageAt ? 1 : -1))
            : prev,
        );
      }
    });
    return off;
  }, [activeId]);

  // Auto-scroll on new messages in active thread.
  useEffect(() => {
    if (!activeId) return;
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeId, threadState]);

  const filteredConversations = useMemo(() => conversations ?? [], [conversations]);
  const activeConv = useMemo(
    () => filteredConversations.find((c) => c.id === activeId) ?? null,
    [filteredConversations, activeId],
  );
  const activeMessages = activeId ? threadState[activeId]?.messages ?? [] : [];

  // Compose new conversation: prompt for user email/UUID.
  const composeNew = async () => {
    const input = window.prompt('Enter user ID (UUID) to open a chat with:');
    if (!input) return;
    const res = await api.openChatConversation(input.trim());
    if (res.success && res.data) {
      await loadConversations();
      setActiveId(res.data.conversation.id);
    } else {
      toastApiError(res, 'Could not open conversation');
    }
  };

  const handleFile = async (file: File) => {
    setUploadError(null);
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File is too large');
      return;
    }
    const res = await api.uploadChatAttachment(file);
    if (!res.success || !res.data) {
      setUploadError(res.error?.message ?? 'Upload failed');
      return;
    }
    setPendingAttachments((prev) => [...prev, res.data!.attachment]);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeId) return;
    const body = draft.trim();
    if (!body && pendingAttachments.length === 0) return;
    if (sending) return;
    setSending(true);
    const nonce =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `nonce-${Date.now()}-${Math.random()}`;
    const optimistic: ChatMessage = {
      id: `optimistic-${nonce}`,
      conversationId: activeId,
      senderType: 'admin',
      senderUserId: null,
      body: body || null,
      i18nKey: null,
      hasAttachments: pendingAttachments.length > 0,
      attachments: pendingAttachments,
      createdAt: new Date().toISOString(),
      readByUserAt: null,
      readByAdminAt: null,
      clientNonce: nonce,
    };
    setThreadState((s) => ({
      ...s,
      [activeId]: {
        loading: false,
        hasMore: s[activeId]?.hasMore ?? true,
        messages: [...(s[activeId]?.messages ?? []), optimistic],
      },
    }));
    setDraft('');
    const attachmentIds = pendingAttachments.map((a) => a.id);
    setPendingAttachments([]);
    const res = await api.postChatConversationMessage(activeId, {
      body: body || undefined,
      clientNonce: nonce,
      attachmentIds: attachmentIds.length ? attachmentIds : undefined,
    });
    setSending(false);
    if (res.success && res.data) {
      setThreadState((s) => {
        const cur = s[activeId];
        if (!cur) return s;
        return {
          ...s,
          [activeId]: {
            ...cur,
            messages: cur.messages.map((m) =>
              m.clientNonce === nonce ? { ...res.data!.message, clientNonce: nonce } : m,
            ),
          },
        };
      });
    } else {
      toastApiError(res, 'Failed to send');
    }
  };

  if (error === 'forbidden') return <Forbidden />;

  return (
    <div
      className="flex h-[calc(100dvh-200px)] lg:h-[calc(100vh-220px)] min-h-[420px]"
      style={{ border: `1px solid ${RULE}` }}
    >
      {/* Left pane — conversation list */}
      <aside
        className={`${
          threadOpen ? 'hidden md:flex' : 'flex'
        } w-full md:w-[340px] lg:w-[360px] shrink-0 flex-col min-w-0`}
        style={{
          borderRight: `1px solid ${RULE}`,
          background: '#fff',
        }}
      >
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${RULE}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ color: INK, letterSpacing: '-0.01em' }}>Conversations</strong>
            <button
              type="button"
              onClick={() => void composeNew()}
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                padding: '4px 10px',
                border: `1px solid ${RULE_STRONG}`,
                background: 'transparent',
                cursor: 'pointer',
                color: INK,
              }}
            >
              + New
            </button>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name"
            style={{
              padding: '8px 10px',
              border: `1px solid ${RULE_STRONG}`,
              background: PAPER,
              fontSize: 13,
              color: INK,
            }}
          />
          <div style={{ display: 'flex', gap: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {(['all', 'unread'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                style={{
                  flex: 1,
                  padding: '5px 0',
                  background: filter === f ? INK : 'transparent',
                  color: filter === f ? PAPER : INK,
                  border: `1px solid ${RULE_STRONG}`,
                  cursor: 'pointer',
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {conversations === null && (
            <div style={{ padding: 20, color: MUTED, fontSize: 13 }}>Loading…</div>
          )}
          {conversations !== null && filteredConversations.length === 0 && (
            <div style={{ padding: 20, color: MUTED, fontSize: 13 }}>No conversations</div>
          )}
          {filteredConversations.map((c) => {
            const isActive = c.id === activeId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setActiveId(c.id);
                  setThreadOpen(true);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: 10,
                  padding: '12px 14px',
                  background: isActive ? '#FFF7EC' : 'transparent',
                  border: 'none',
                  borderBottom: `1px solid ${RULE}`,
                  borderLeft: isActive ? `3px solid ${AMBER}` : '3px solid transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    background: INK,
                    color: PAPER,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {initialsOf(c.user.firstName, c.user.lastName, c.user.email)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: INK,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {c.user.firstName || c.user.lastName
                        ? `${c.user.firstName ?? ''} ${c.user.lastName ?? ''}`.trim()
                        : c.user.email}
                    </span>
                    <span style={{ fontSize: 10, color: MUTED, flexShrink: 0 }}>{fmtRel(c.lastMessageAt)}</span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: MUTED,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c.lastMessagePreview ?? ''}
                  </div>
                  {c.unreadCountAdmin > 0 && (
                    <span
                      style={{
                        display: 'inline-block',
                        marginTop: 4,
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: 8,
                        background: AMBER,
                        color: '#fff',
                      }}
                    >
                      {c.unreadCountAdmin}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Right pane — thread */}
      <section
        className={`${threadOpen ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`}
        style={{ background: PAPER }}
      >
        {!activeConv ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: 13 }}>
            Select a conversation
          </div>
        ) : (
          <>
            <header
              className="flex items-center gap-2"
              style={{ padding: '14px 18px', borderBottom: `1px solid ${RULE}`, background: '#fff' }}
            >
              <button
                type="button"
                onClick={() => setThreadOpen(false)}
                aria-label="Back to conversations"
                className="md:hidden -ml-2 p-2 shrink-0"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: INK, fontSize: 16 }}
              >
                ←
              </button>
              <div className="min-w-0">
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: INK,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {activeConv.user.firstName || activeConv.user.lastName
                    ? `${activeConv.user.firstName ?? ''} ${activeConv.user.lastName ?? ''}`.trim()
                    : activeConv.user.email}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: MUTED,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {activeConv.user.email}
                </div>
              </div>
            </header>
            <div
              ref={scrollRef}
              style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              {activeMessages.length === 0 && (
                <div style={{ color: MUTED, fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No messages yet
                </div>
              )}
              {activeMessages.map((m) => {
                const mine = m.senderType === 'admin';
                const display =
                  m.senderType === 'system' && m.i18nKey === 'chat.welcome.driverforce_team'
                    ? 'Welcome message (sent automatically)'
                    : m.body ?? '';
                return (
                  <div
                    key={m.id}
                    className="max-w-[85%] md:max-w-[70%]"
                    style={{
                      alignSelf: mine ? 'flex-end' : 'flex-start',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        background: mine ? INK : '#fff',
                        color: mine ? PAPER : INK,
                        border: mine ? 'none' : `1px solid ${RULE}`,
                        padding: '8px 12px',
                        borderRadius: 12,
                        fontSize: 13,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {display && <div>{display}</div>}
                      {m.attachments.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                          {m.attachments.map((a) => (
                            <AdminAttachmentLink key={a.id} attachment={a} />
                          ))}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: 10, color: MUTED, textAlign: mine ? 'right' : 'left' }}>
                      {mine ? 'You' : activeConv.user.email} · {fmtTime(m.createdAt)}
                    </span>
                  </div>
                );
              })}
            </div>
            {pendingAttachments.length > 0 && (
              <div style={{ padding: '6px 18px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {pendingAttachments.map((a) => (
                  <span
                    key={a.id}
                    style={{
                      fontSize: 11,
                      background: '#fff',
                      border: `1px solid ${RULE_STRONG}`,
                      borderRadius: 999,
                      padding: '3px 9px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {a.originalFilename}
                    <button
                      type="button"
                      onClick={() =>
                        setPendingAttachments((prev) => prev.filter((x) => x.id !== a.id))
                      }
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            {uploadError && (
              <div style={{ padding: '4px 18px', color: '#B00020', fontSize: 12 }}>{uploadError}</div>
            )}
            <form
              onSubmit={handleSend}
              style={{ padding: 12, borderTop: `1px solid ${RULE}`, background: '#fff', display: 'flex', gap: 8, alignItems: 'flex-end' }}
            >
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void handleFile(f);
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '8px 6px', color: MUTED }}
                aria-label="Attach"
              >
                📎
              </button>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type a message…"
                rows={2}
                maxLength={4000}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  border: `1px solid ${RULE_STRONG}`,
                  background: PAPER,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'none',
                  color: INK,
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend(e);
                  }
                }}
              />
              <button
                type="submit"
                disabled={sending || (!draft.trim() && pendingAttachments.length === 0)}
                style={{
                  background: INK,
                  color: PAPER,
                  border: 'none',
                  padding: '10px 14px',
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  cursor: 'pointer',
                  opacity: sending || (!draft.trim() && pendingAttachments.length === 0) ? 0.5 : 1,
                }}
              >
                Send
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
};

const AdminAttachmentLink: React.FC<{ attachment: ChatAttachment }> = ({ attachment }) => {
  const open = async () => {
    const res = await api.getChatAttachmentDownload(attachment.id);
    if (res.success && res.data) {
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    }
  };
  return (
    <button
      type="button"
      onClick={open}
      style={{
        background: 'rgba(0,0,0,0.04)',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 8,
        padding: '4px 8px',
        cursor: 'pointer',
        fontSize: 11,
        textAlign: 'left',
        color: 'inherit',
      }}
    >
      📎 {attachment.originalFilename}
    </button>
  );
};
